/*!
 * True Cost Widget — Petit Pli
 * Vanilla JS, no dependencies. ES2017+.
 */

(function () {
  'use strict';

  // Eurozone country codes
  const EUR_COUNTRIES = new Set([
    'AT','BE','CY','EE','FI','FR','DE','GR','IE','IT',
    'LV','LT','LU','MT','NL','PT','SK','SI','ES','HR',
    'MC','SM','VA'
  ]);

  // ── Currency detection ────────────────────────────────────────────────────────

  function detectCurrency(config) {
    if (!config.currencies) return Promise.resolve('GBP');
    return fetch('https://ipapi.co/json/', { cache: 'no-store' })
      .then(r => r.json())
      .then(data => {
        const cc = (data.country_code || '').toUpperCase();
        if (cc === 'US') return 'USD';
        if (EUR_COUNTRIES.has(cc)) return 'EUR';
        return 'GBP';
      })
      .catch(() => 'GBP');
  }

  // Apply currency overrides onto config in-place
  function applyCurrency(config, code) {
    const cur = (config.currencies || {})[code] || config.currencies['GBP'];
    config._currencySymbol     = cur.symbol;
    config._currencyCode       = code;
    config.petitPliComparison.price = cur.ppPrice;
    config.slider              = { ...config.slider, ...cur.slider };
    config.shippingCostPerOrder = cur.shippingCost;
    config.vinted.threshold    = cur.vintedThreshold;
  }

  // ── Widget ──────────────────────────────────────────────────────────────────

  class TrueCostWidget {
    constructor(el, config) {
      this.el        = el;
      this.config    = config;
      this._price    = config.slider.default;
      this._noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this._sym      = config._currencySymbol || '£';
      this._fmt      = (n) => `${this._sym}${n}`;
      this._render();
    }

    // ── Analytics ─────────────────────────────────────────────────────────────

    _fire(name, props) {
      try {
        if (typeof window.gtag === 'function') window.gtag('event', name, props);
        if (typeof window.fbq  === 'function') window.fbq('trackCustom', name, props);
      } catch (_) {}
    }

    // ── Render ────────────────────────────────────────────────────────────────

    _render() {
      const { slider, petitPliComparison: pp, benefitMatrix, testimonial } = this.config;
      const f = this._fmt;

      this.el.innerHTML = `
        <div class="tcw-inner" role="region" aria-label="True cost of kidswear">

          <div class="tcw-header">
            <p class="tcw-eyebrow">The real cost of kidswear</p>
            <h2 class="tcw-headline">How far does your ${this._sym} actually go?</h2>
            <p class="tcw-subhead">Drag the slider to your usual spend per garment and see how the numbers compare.</p>
          </div>

          <div class="tcw-slider-section">
            <div class="tcw-price-display" id="tcw-price-display" aria-live="polite" aria-atomic="true">
              <span class="tcw-price-amount">${f(this._price)}</span>
              <span class="tcw-price-label">What do you pay per garment?</span>
            </div>
            <div class="tcw-slider-wrap">
              <input
                type="range"
                class="tcw-slider"
                id="tcw-slider"
                min="${slider.min}"
                max="${slider.max}"
                step="${slider.step}"
                value="${this._price}"
                aria-label="Average price per garment elsewhere, ${this._sym}${slider.min} to ${this._sym}${slider.max}"
              >
              <div class="tcw-slider-rails" aria-hidden="true">
                <span>${f(slider.min)}</span>
                <span>${f(slider.max)}</span>
              </div>
              <p class="tcw-slider-hint" aria-hidden="true">drag to your usual spend</p>
            </div>
          </div>

          <div class="tcw-comparison" id="tcw-comparison" aria-live="polite" aria-atomic="true">
            ${this._comparisonHTML()}
          </div>

          <div class="tcw-matrix">
            <p class="tcw-matrix-title">Why parents choose Petit Pli</p>
            <div class="tcw-matrix-table" role="table" aria-label="Feature comparison">
              <div class="tcw-mrow tcw-mrow--head" role="row" aria-hidden="true">
                <div class="tcw-mc tcw-mc--feat" role="columnheader"></div>
                <div class="tcw-mc tcw-mc--other" role="columnheader">Other brands</div>
                <div class="tcw-mc tcw-mc--pp" role="columnheader">Petit Pli</div>
              </div>
              ${benefitMatrix.map(row => `
                <div class="tcw-mrow${row.highlight ? ' tcw-mrow--highlight' : ''}" role="row">
                  <div class="tcw-mc tcw-mc--feat" role="cell">${row.feature}</div>
                  <div class="tcw-mc tcw-mc--other" role="cell" aria-label="Other brands: ${this._cellLabel(row.other)}">${this._cellHTML(row.other)}</div>
                  <div class="tcw-mc tcw-mc--pp"    role="cell" aria-label="Petit Pli: ${this._cellLabel(row.petitPli)}">${this._cellHTML(row.petitPli)}</div>
                </div>
              `).join('')}
            </div>
          </div>

          <blockquote class="tcw-quote">
            <p class="tcw-quote-text">&ldquo;${testimonial.quote}&rdquo;</p>
            <cite class="tcw-quote-cite">${testimonial.attributionUrl
              ? `<a href="${testimonial.attributionUrl}" target="_blank" rel="noopener" class="tcw-quote-link">${testimonial.attribution}</a>`
              : testimonial.attribution
            }</cite>
          </blockquote>

        </div>`;

      this._bindSlider();

    }

    // ── Slider ────────────────────────────────────────────────────────────────

    _bindSlider() {
      const slider = document.getElementById('tcw-slider');
      if (!slider) return;

      this._setFill(slider, this._price);

      slider.addEventListener('input', () => {
        this._price = parseInt(slider.value, 10);
        this._setFill(slider, this._price);
        const display = document.getElementById('tcw-price-display');
        if (display) display.querySelector('.tcw-price-amount').textContent = this._fmt(this._price);
        const comp = document.getElementById('tcw-comparison');
        if (comp) comp.innerHTML = this._comparisonHTML();
      });

      slider.addEventListener('change', () => {
        this._fire('petitpli_widget_slider_changed', {
          currency: this.config._currencyCode || 'GBP',
          price: this._price,
        });
      });
    }

    _setFill(slider, value) {
      const { min, max } = this.config.slider;
      const pct = ((value - min) / (max - min)) * 100;
      slider.style.setProperty('--fill', `${pct}%`);
    }

    // ── Comparison block (live-updated) ───────────────────────────────────────

    _comparisonHTML() {
      const { petitPliComparison: pp, sizeBrackets: brackets, sustainabilityStat: eco } = this.config;
      const f               = this._fmt;
      const price           = this._price;
      const competitorTotal = price * brackets.length;
      const saving          = competitorTotal - pp.price;
      const ppPerSize       = (pp.price / brackets.length).toFixed(2);
      const garmentsSaved   = brackets.length - 1;
      const shippingRate    = this.config.shippingCostPerOrder || 3.99;
      const shippingSaving  = (shippingRate * brackets.length).toFixed(2);

      const { vinted } = this.config;
      const belowThreshold = price < vinted.threshold;

      const savingBlock = belowThreshold
        ? `<div class="tcw-saving tcw-saving--vinted">
             <span class="tcw-saving-figure">Looking for a bargain?</span>
             <span class="tcw-saving-note">At this price point, new may not beat pre-loved.
               <a class="tcw-vinted-link" href="${vinted.url}" target="_blank" rel="noopener">
                 Find a Petit Pli on our Vinted →
               </a>
             </span>
           </div>`
        : saving > 0
        ? `<div class="tcw-saving tcw-saving--positive">
             <span class="tcw-saving-label">Buying those 7 sizes elsewhere costs</span>
             <span class="tcw-saving-figure">${f(competitorTotal)}</span>
             <span class="tcw-saving-contrast">Petit Pli covers it all for ${f(pp.price)}: 7 sizes, one decision.</span>
             <span class="tcw-saving-note">${garmentsSaved} fewer purchases · ≈${eco.co2SavedKg}&thinsp;kg CO₂ saved<sup>*</sup></span>
           </div>`
        : `<div class="tcw-saving tcw-saving--neutral">
             <span class="tcw-saving-figure">Same cost, smarter decision</span>
             <span class="tcw-saving-note">One Petit Pli covers all ${brackets.length} sizes vs ${brackets.length} separate purchases</span>
           </div>`;

      return `
        <div class="tcw-cards">
          <div class="tcw-card tcw-card--other">
            <span class="tcw-card-brand">Other brands</span>
            <span class="tcw-card-count">${brackets.length} garments over 4 years</span>
            <span class="tcw-card-total">${f(competitorTotal)}</span>
            <span class="tcw-card-per">${f(price)} per size</span>
            <span class="tcw-card-shipping tcw-card-shipping--other">+ ${this._sym}${shippingRate.toFixed(2)} × ${brackets.length} = ${this._sym}${shippingSaving} shipping</span>
          </div>
          <div class="tcw-card tcw-card--pp">
            <span class="tcw-card-brand">Petit Pli</span>
            <span class="tcw-card-count">1 garment, 4 years</span>
            <span class="tcw-card-total">${f(pp.price)}</span>
            <span class="tcw-card-per">${this._sym}${ppPerSize} per size</span>
          </div>
        </div>
        ${savingBlock}
        <p class="tcw-eco-source" aria-label="Source">*${eco.source}</p>`;
    }

    // ── Matrix helpers ────────────────────────────────────────────────────────

    _cellHTML(value) {
      if (value === true)     return `<span class="tcw-cell tcw-cell--yes">✓</span>`;
      if (value === false)    return `<span class="tcw-cell tcw-cell--no">✗</span>`;
      if (value === 'varies') return `<span class="tcw-cell tcw-cell--varies">varies</span>`;
      return `<span class="tcw-cell tcw-cell--varies">?</span>`;
    }

    _cellLabel(value) {
      if (value === true)     return 'Yes';
      if (value === false)    return 'No';
      if (value === 'varies') return 'Varies';
      return 'Unknown';
    }
  }

  // ── Bootstrap ────────────────────────────────────────────────────────────────

  function fetchConfig(url) {
    return fetch(url).then(r => {
      if (!r.ok) throw new Error(`[TrueCostWidget] config fetch failed (${r.status})`);
      return r.json();
    });
  }

  function init() {
    const targets = [...document.querySelectorAll('[data-true-cost-widget]')];
    if (!targets.length) return;

    const configUrl = targets[0].dataset.config || 'config.json';

    fetchConfig(configUrl)
    .then(config => {
      // Detect currency with a 2-second timeout — always falls back to GBP
      return Promise.race([
        detectCurrency(config),
        new Promise(resolve => setTimeout(() => resolve('GBP'), 2000))
      ]).then(currency => {
        applyCurrency(config, currency);
        return config;
      });
    })
    .then(config => {
      const mount = (el) => new TrueCostWidget(el, config);

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) return;
            mount(entry.target);
            obs.unobserve(entry.target);
          });
        }, { rootMargin: '200px' });
        targets.forEach(el => io.observe(el));
      } else {
        targets.forEach(mount);
      }
    })
    .catch(err => console.error(err));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.TrueCostWidget = TrueCostWidget;
  window._applyCurrency = applyCurrency; // exposed for manual override / testing
})();
